import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Lang } from '../types';
interface BookingToastProps {
  lang: Lang;
}
const names = ['Sofia', 'James', 'María', 'David', 'Isabella', 'Carlos', 'Emma', 'Miguel', 'Olivia', 'Alejandro', 'Mia', 'Daniel', 'Valentina', 'Ryan', 'Camila', 'Tyler', 'Lucía', 'Brandon', 'Andrea', 'Jason', 'Ana', 'Kevin', 'Gabriela', 'Chris', 'Laura'];
const cities = ['Miami', 'Los Angeles', 'New York', 'Houston', 'Chicago', 'Phoenix', 'San Antonio', 'Dallas', 'Austin', 'San Diego', 'Orlando', 'Tampa', 'Denver', 'Atlanta', 'Las Vegas', 'Portland', 'Seattle', 'Nashville', 'Charlotte', 'San Francisco'];
const services = [{
  id: 'fine-line',
  en: 'Fine Line tattoo',
  es: 'tatuaje Fine Line',
  image: 'https://cdn.magicpatterns.com/uploads/3ufoLyBwjPRQ1hA4YmWmiX/467879966_18028434881393787_5776615547924424798_n.jpg'
}, {
  id: 'realistic',
  en: 'Realistic tattoo',
  es: 'tatuaje Realista',
  image: 'https://cdn.magicpatterns.com/uploads/bv3TG6bDtiGVrfnMnqnaah/484370911_18160809022341686_1779723690504867755_n.jpg'
}, {
  id: 'mandala',
  en: 'Mandala tattoo',
  es: 'tatuaje Mandala',
  image: 'https://cdn.magicpatterns.com/uploads/qpsCSwksYiZBmc9TmSHvR5/489034991_18163249150341686_693240776012045429_n.jpg'
}, {
  id: 'color',
  en: 'Color tattoo',
  es: 'tatuaje a Color',
  image: 'https://cdn.magicpatterns.com/uploads/gYUG5wL5gDj8SH8Cm4eGGn/491225603_1748095029108086_240616346185405254_n.jpg'
}, {
  id: 'neo-traditional',
  en: 'Neo-traditional tattoo',
  es: 'tatuaje Neotradicional',
  image: 'https://cdn.magicpatterns.com/uploads/up7jrv21RWHEJpMp422yof/491325230_18164465830341686_7961157127155896631_n.jpg'
}];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randomInterval(): number {
  return Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
}
export function BookingToast({
  lang
}: BookingToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [toastData, setToastData] = useState<{
    name: string;
    city: string;
    service: (typeof services)[0];
  } | null>(null);
  const recentNamesRef = useRef<string[]>([]);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const shouldReduceMotion = useReducedMotion();
  const showToast = useCallback(() => {
    const availableNames = names.filter(n => !recentNamesRef.current.includes(n));
    const name = availableNames.length > 0 ? pickRandom(availableNames) : pickRandom(names);
    recentNamesRef.current = [name, ...recentNamesRef.current].slice(0, 10);
    setToastData({
      name,
      city: pickRandom(cities),
      service: pickRandom(services)
    });
    setIsVisible(true);
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      showTimeoutRef.current = setTimeout(showToast, randomInterval());
    }, 4000);
  }, []);
  useEffect(() => {
    showTimeoutRef.current = setTimeout(showToast, 3000);
    return () => {
      clearTimeout(showTimeoutRef.current);
      clearTimeout(hideTimeoutRef.current);
    };
  }, [showToast]);
  const handleDismiss = useCallback(() => {
    clearTimeout(hideTimeoutRef.current);
    setIsVisible(false);
    showTimeoutRef.current = setTimeout(showToast, randomInterval());
  }, [showToast]);
  const motionVariants = {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 80
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 80
    }
  };
  return <AnimatePresence data-id="element-72">
      {isVisible && toastData && <motion.div variants={motionVariants} initial="initial" animate="animate" exit="exit" transition={{
      type: 'spring',
      stiffness: 400,
      damping: 30
    }} onClick={handleDismiss} className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-2rem)] max-w-[380px] bg-studio-bgAlt border border-studio-border rounded-lg p-3 shadow-xl shadow-black/30 flex items-center gap-3 cursor-pointer hover:bg-studio-border/30 transition-colors" role="status" aria-live="polite" data-id="element-73">
          <img src={toastData.service.image} alt="" className="w-12 h-12 rounded-md object-cover flex-shrink-0 border border-studio-border" data-id="element-74" />
          <div className="flex-1 min-w-0" data-id="element-75">
            <p className="text-sm font-medium text-studio-text truncate" data-id="element-76">
              {toastData.name} {lang === 'en' ? 'in' : 'en'} {toastData.city}
            </p>
            <p className="text-xs text-studio-muted truncate" data-id="element-77">
              {lang === 'en' ? `just booked a ${toastData.service.en} session` : `acaba de reservar una sesión de ${toastData.service.es}`}
            </p>
            <div className="flex items-center gap-1.5 mt-1" data-id="element-78">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" aria-hidden="true" data-id="element-79" />
              <span className="text-[10px] text-studio-accent uppercase tracking-wider font-semibold" data-id="element-80">
                {lang === 'en' ? 'Just now' : 'Hace un momento'}
              </span>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}